import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";

const Ctx = createContext(null);

export function BackgroundMusicProvider({ children }) {
  const [queue, setQueue] = useState([]); // [{id, title, artist, youtube_id}]
  const [index, setIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [shuffle, setShuffle] = useState(false);
  const [volume, setVolume] = useState(60);
  const [minimized, setMinimized] = useState(false);
  // Map of videoIds that errored — used so the background player can fall
  // back to a YouTube search-based embed for the same track when the
  // uploader disables embedding or the video gets removed.
  const [deadIds, setDeadIds] = useState({});
  const playerRef = useRef(null); // YT.Player instance

  const onPlayerReady = useCallback((player) => {
    playerRef.current = player;
    try { player.setVolume(volume); } catch {}
  }, [volume]);

  const onPlayerStateChange = useCallback((state) => {
    // YT.PlayerState: -1 unstarted, 0 ended, 1 playing, 2 paused, 3 buffering, 5 cued
    if (state === 1) {
      setIsPlaying(true);
      // The YouTube player advances the native playlist itself (this is
      // what makes auto-advance work on MOBILE — it stays inside the
      // user's original tap). Sync our UI index to whatever it's on.
      const p = playerRef.current;
      try {
        const pi = p?.getPlaylistIndex?.();
        if (typeof pi === "number" && pi >= 0) setIndex(pi);
      } catch {}
    } else if (state === 2) {
      setIsPlaying(false);
    }
  }, []);

  // YouTube error codes: 2 invalid id, 5 html5 player error, 100 video removed,
  // 101/150 embed not allowed by uploader. Mark the id as dead (the native
  // playlist auto-skips unplayable videos, so this is just for UI/telemetry).
  const onPlayerError = useCallback(() => {
    const cur = queue[index];
    if (cur?.youtube_id) setDeadIds((m) => ({ ...m, [cur.youtube_id]: true }));
  }, [queue, index]);

  const playList = useCallback((list, startIndex = 0) => {
    // Pause any Bible read-aloud / TTS that may be running so we don't
    // play two voices at once. The AudioContext listens for this event
    // and stops itself.
    try { window.dispatchEvent(new CustomEvent("hw:bgmusic:start")); } catch (_) {}
    setQueue(list);
    setIndex(Math.min(startIndex, Math.max(list.length - 1, 0)));
    setIsPlaying(true);
    setMinimized(false);
  }, []);

  const playSingle = useCallback((track) => {
    try { window.dispatchEvent(new CustomEvent("hw:bgmusic:start")); } catch (_) {}
    setQueue([track]);
    setIndex(0);
    setIsPlaying(true);
    setMinimized(false);
  }, []);

  const togglePlay = useCallback(() => {
    const p = playerRef.current;
    if (!p) { setIsPlaying((v) => !v); return; }
    try {
      if (isPlaying) p.pauseVideo(); else p.playVideo();
    } catch {}
  }, [isPlaying]);

  const next = useCallback(() => {
    if (queue.length === 0) return;
    // Drive the native YouTube playlist so this counts as a user
    // gesture on mobile (loadVideoById-style jumps get blocked there).
    try { playerRef.current?.nextVideo?.(); } catch {}
    setIsPlaying(true);
  }, [queue.length]);

  const prev = useCallback(() => {
    if (queue.length === 0) return;
    try { playerRef.current?.previousVideo?.(); } catch {}
    setIsPlaying(true);
  }, [queue.length]);

  const stop = useCallback(() => {
    setIsPlaying(false);
    setQueue([]);
    setIndex(0);
    try { playerRef.current?.stopVideo(); } catch {}
  }, []);

  // Sync volume to player when changed
  useEffect(() => {
    try { playerRef.current?.setVolume(volume); } catch {}
  }, [volume]);

  // Sync shuffle to the native YouTube playlist.
  useEffect(() => {
    try { playerRef.current?.setShuffle?.(shuffle); } catch {}
  }, [shuffle]);

  // Cross-context audio bus — pause the gospel background mix when
  // any other audio surface starts (Bible read-aloud, sermon, etc.)
  // so the user never hears two voices on top of each other.
  useEffect(() => {
    const pause = () => {
      const p = playerRef.current;
      try { p?.pauseVideo?.(); } catch (_) {}
      setIsPlaying(false);
    };
    window.addEventListener("hw:audio:start", pause);
    return () => window.removeEventListener("hw:audio:start", pause);
  }, []);

  const current = queue[index] || null;
  // Tell the mini-player whether the current track's videoId is dead, so it
  // can render a fallback search iframe instead.
  const currentDead = !!(current && deadIds[current.youtube_id]);

  return (
    <Ctx.Provider value={{
      queue, index, current, currentDead, isPlaying, shuffle, volume, minimized,
      setShuffle, setVolume, setMinimized,
      playList, playSingle, togglePlay, next, prev, stop,
      onPlayerReady, onPlayerStateChange, onPlayerError,
    }}>
      {children}
    </Ctx.Provider>
  );
}

export function useBgMusic() {
  return useContext(Ctx);
}
